import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  changeFirstPassword,
  changeFirstPasswordManual,
  getFirstLoginInfo,
} from '../../services/api';
import '../../styles/ChangePassword.css';
import backgroundImage from '../../assets/images/background.png';
import logo from '../../assets/images/itup_logo.png';

export default function ChangePassword() {
  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    let value = params.get('param') || '';
    if (!value && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1];
      if (hashQuery) {
        value = new URLSearchParams(hashQuery).get('param') || '';
      }
    }
    return value;
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [manualAliasAccess, setManualAliasAccess] = useState('');
  const [manualUsername, setManualUsername] = useState('');
  const [manualOldPassword, setManualOldPassword] = useState('');
  const [showManualOldPassword, setShowManualOldPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(token ? 'checking' : 'missing');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    let active = true;

    if (!token) {
      setTokenStatus('missing');
      return () => {
        active = false;
      };
    }

    setTokenStatus('checking');
    getFirstLoginInfo({ token })
      .then(({ data }) => {
        if (!active) return;
        const name = data?.name || data?.username || '';
        setDisplayName(name);
        setTokenStatus('valid');
      })
      .catch((err) => {
        if (!active) return;
        setTokenStatus('invalid');
        setError(
          err.response?.data?.error ||
            'Token inválido ou expirado. Solicite um novo link.',
        );
      });

    return () => {
      active = false;
    };
  }, [token]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (
      !manualAliasAccess ||
      !manualUsername ||
      !manualOldPassword ||
      !password ||
      !passwordConfirm
    ) {
      setError('Preencha todos os campos.');
      return;
    }

    if (password !== passwordConfirm) {
      setError('As senhas não conferem.');
      return;
    }

    try {
      setLoading(true);
      await changeFirstPasswordManual({
        aliasAccess: manualAliasAccess,
        username: manualUsername,
        oldPassword: manualOldPassword,
        password,
        passwordConfirm,
      });
      setSuccess('Senha alterada com sucesso. Você já pode fazer login.');
      setManualAliasAccess('');
      setManualUsername('');
      setManualOldPassword('');
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar senha.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (tokenStatus !== 'valid') {
      setError('Token inválido ou ausente. Solicite um novo link.');
      return;
    }

    if (!email || !password || !passwordConfirm) {
      setError('Preencha todos os campos.');
      return;
    }

    if (password !== passwordConfirm) {
      setError('As senhas não conferem.');
      return;
    }

    try {
      setLoading(true);
      await changeFirstPassword({ token, email, password, passwordConfirm });
      setSuccess('Senha alterada com sucesso. Você já pode fazer login.');
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="login-page"
      sx={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box className="login-box">
        <img src={logo} alt="Rental Itup Logo" className="login-logo" />
        <Typography className="login-title">
          <span>Alterar</span>
          <span className="login-title-bold">&nbsp;Senha de Primeiro Acesso</span>
        </Typography>

        {tokenStatus === 'missing' && (
          <Alert severity="info" className="change-alert">
            Informe seu token, usuário e senha atual para definir uma nova senha.
          </Alert>
        )}

        {tokenStatus === 'checking' && (
          <Alert severity="info" className="change-alert">
            Validando link de primeiro acesso...
          </Alert>
        )}

        {error && (
          <Alert severity="error" className="change-alert">
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" className="change-alert">
            {success}
          </Alert>
        )}

        {tokenStatus === 'valid' && displayName && (
          <Typography className="change-greeting">
            Olá, <strong>{displayName}</strong>. Preencha seu e-mail e sua
            nova senha.
          </Typography>
        )}

        {tokenStatus === 'valid' && (
          <Box component="form" onSubmit={handleSubmit} className="login-form">
            <Box className="input-group">
              <Typography className="input-label">E-mail</Typography>
              <TextField
                variant="outlined"
                size="small"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-input"
              />
            </Box>

            <Box className="input-group">
              <Typography className="input-label">Nova senha</Typography>
              <TextField
                variant="outlined"
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className="input-group">
              <Typography className="input-label">Confirmar senha</Typography>
              <TextField
                variant="outlined"
                size="small"
                type={showPasswordConfirm ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                className="login-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswordConfirm((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPasswordConfirm ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              className="login-button"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar senha'}
            </Button>
          </Box>
        )}

        {tokenStatus === 'missing' && (
          <Box component="form" onSubmit={handleManualSubmit} className="login-form">
            <Box className="input-group">
              <Typography className="input-label">Token</Typography>
              <TextField
                variant="outlined"
                size="small"
                value={manualAliasAccess}
                onChange={(e) => setManualAliasAccess(e.target.value)}
                required
                className="login-input"
              />
            </Box>

            <Box className="input-group">
              <Typography className="input-label">Usuário</Typography>
              <TextField
                variant="outlined"
                size="small"
                value={manualUsername}
                onChange={(e) => setManualUsername(e.target.value)}
                required
                className="login-input"
              />
            </Box>

            <Box className="input-group">
              <Typography className="input-label">Senha atual</Typography>
              <TextField
                variant="outlined"
                size="small"
                type={showManualOldPassword ? 'text' : 'password'}
                value={manualOldPassword}
                onChange={(e) => setManualOldPassword(e.target.value)}
                required
                className="login-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowManualOldPassword((prev) => !prev)
                        }
                        edge="end"
                        size="small"
                      >
                        {showManualOldPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className="input-group">
              <Typography className="input-label">Nova senha</Typography>
              <TextField
                variant="outlined"
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className="input-group">
              <Typography className="input-label">Confirmar senha</Typography>
              <TextField
                variant="outlined"
                size="small"
                type={showPasswordConfirm ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                className="login-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswordConfirm((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPasswordConfirm ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              className="login-button"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar senha'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
